import './TournamentBracket.css';

/* ─────────────────────────────────────────────────────────────
   TOURNAMENT BRACKET component
   Renders a single-elimination bracket left → right.
   Works for 4, 8, and 16-player (or team) tournaments.

   Props:
     matches      — array of tournament_match rows
     playerCount  — 4 | 8 | 16 (team count for doubles)
     isCreator    — boolean, shows "Submit Score" button when true
     onSubmit     — fn(match) called when creator clicks Submit Score
     isDoubles    — boolean, renders team names instead of player names
     teamMap      — { [team_id]: { player1, player2 } } for doubles
   ───────────────────────────────────────────────────────────── */
function TournamentBracket({ matches, playerCount, isCreator, onSubmit, isDoubles = false, teamMap = {} }) {
  const totalRounds = Math.log2(playerCount);

  // Group matches by round
  const rounds = [];
  for (let r = 1; r <= totalRounds; r++) {
    rounds.push(matches.filter(m => m.round === r).sort((a, b) => a.match_number - b.match_number));
  }

  const roundLabels = getRoundLabels(totalRounds);

  return (
    <div className="bracket-scroll">
      <div className="bracket-wrap" style={{ '--total-rounds': totalRounds }}>
        {rounds.map((roundMatches, rIdx) => (
          <div key={rIdx} className="bracket-round">
            <p className="bracket-round-label">{roundLabels[rIdx]}</p>
            <div className="bracket-round-matches" style={{ '--match-count': roundMatches.length }}>
              {roundMatches.map(match => (
                <MatchCard
                  key={match.id}
                  match={match}
                  isCreator={isCreator}
                  onSubmit={onSubmit}
                  round={rIdx + 1}
                  totalRounds={totalRounds}
                  isDoubles={isDoubles}
                  teamMap={teamMap}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Single match card ─────────────────────────────────────── */
function MatchCard({ match, isCreator, onSubmit, isDoubles, teamMap }) {
  if (isDoubles) {
    const team1   = match.team1_id ? teamMap[match.team1_id] : null;
    const team2   = match.team2_id ? teamMap[match.team2_id] : null;
    const hasTeams = team1 && team2;
    const isDone   = match.status === 'completed';

    return (
      <div className={`bracket-match ${isDone ? 'done' : ''}`}>
        <TeamSlot
          team={team1}
          isWinner={isDone && match.winner_team_id === match.team1_id}
          score={isDone ? match.player1_score : null}
        />
        <div className="bracket-match-divider" />
        <TeamSlot
          team={team2}
          isWinner={isDone && match.winner_team_id === match.team2_id}
          score={isDone ? match.player2_score : null}
        />
        {isCreator && hasTeams && !isDone && (
          <button className="bracket-submit-btn" onClick={() => onSubmit(match)}>
            Submit Score
          </button>
        )}
      </div>
    );
  }

  const p1      = match.player1;
  const p2      = match.player2;
  const hasPlayers = p1 && p2;
  const isDone     = match.status === 'completed';

  return (
    <div className={`bracket-match ${isDone ? 'done' : ''}`}>
      <PlayerSlot
        player={p1}
        isWinner={isDone && match.winner_id === match.player1_id}
        score={isDone ? match.player1_score : null}
      />
      <div className="bracket-match-divider" />
      <PlayerSlot
        player={p2}
        isWinner={isDone && match.winner_id === match.player2_id}
        score={isDone ? match.player2_score : null}
      />
      {isCreator && hasPlayers && !isDone && (
        <button className="bracket-submit-btn" onClick={() => onSubmit(match)}>
          Submit Score
        </button>
      )}
    </div>
  );
}

/* ── Singles player slot ───────────────────────────────────── */
function PlayerSlot({ player, isWinner, score }) {
  if (!player) {
    return (
      <div className="bracket-player bracket-player--tbd">
        <span className="bracket-player-name">TBD</span>
      </div>
    );
  }

  return (
    <div className={`bracket-player ${isWinner ? 'bracket-player--winner' : ''}`}>
      <div className="bracket-player-avatar">{player.username[0].toUpperCase()}</div>
      <span className="bracket-player-name">{player.username}</span>
      <span className="bracket-player-elo">{player.elo_rating}</span>
      {score !== null && (
        <span className={`bracket-player-score ${isWinner ? 'winner-score' : ''}`}>{score}</span>
      )}
    </div>
  );
}

/* ── Doubles team slot ─────────────────────────────────────── */
function TeamSlot({ team, isWinner, score }) {
  if (!team) {
    return (
      <div className="bracket-player bracket-player--tbd">
        <span className="bracket-player-name">TBD</span>
      </div>
    );
  }

  const avgElo = Math.round(((team.player1?.doubles_elo ?? 1200) + (team.player2?.doubles_elo ?? 1200)) / 2);
  const label  = `${team.player1?.username ?? '?'} & ${team.player2?.username ?? '?'}`;

  return (
    <div className={`bracket-player ${isWinner ? 'bracket-player--winner' : ''}`}>
      <div className="bracket-player-avatar bracket-team-avatar">
        {(team.player1?.username ?? '?')[0].toUpperCase()}
      </div>
      <span className="bracket-player-name bracket-team-name">{label}</span>
      <span className="bracket-player-elo">{avgElo}</span>
      {score !== null && (
        <span className={`bracket-player-score ${isWinner ? 'winner-score' : ''}`}>{score}</span>
      )}
    </div>
  );
}

/* ── Round label helper ────────────────────────────────────── */
function getRoundLabels(totalRounds) {
  const labels = [];
  for (let r = 1; r <= totalRounds; r++) {
    const fromEnd = totalRounds - r;
    if (fromEnd === 0) labels.push('Final');
    else if (fromEnd === 1) labels.push('Semifinal');
    else if (fromEnd === 2) labels.push('Quarterfinal');
    else labels.push(`Round ${r}`);
  }
  return labels;
}

export default TournamentBracket;
